import { token, net, ui } from "@platform/index"
import { isEmpty } from "../../utils/index"
import { miniStorage } from "../../utils/storage/miniStorage"
import { getSystemErrorLang } from "../i18n/LocaleMap"
import { globalInfo } from "../env/util"
import { _default as rootMax } from '../../max/ctx'
import { Logger } from "../../utils/logger"

type TcacheToken = {
  "max-token": string
  beforeTime: number
  duration: number
}
type Ttokens = Record<string, string>

/**
 * 判断是否开启了登录鉴权
 * @param config
 */
export function loginRule<T extends NetRequest>(config: T): Boolean {
  const url = config?.url
  if (url.indexOf("http://127.0.0.1") !== -1) return true
  if (url.includes("auth/token/getAccessToken")) return false
  if (url.includes("auth/token/queryAccessTokenByAppId")) return false
  if (url.includes("client/auth/obtainInfo")) return true
  // 判断是否开启了登录鉴权，init的参数传递
  const login: Tlogin = globalInfo.get("login") as Tlogin
  if (!login?.isLoginAuth) return false

  const domainList = login?.interceptDomains
  if (!domainList.length) return false
  // 拦截sunmi.com和biot-apps.com
  const istrue = domainList.some((d) => {
    const regex = new RegExp(
      `(https?:\\/\\/(.+?\\.)?${d}(\\/[A-Za-z0-9\\-._~:\\/?#\\[\\]@!$&'\\(\\)*+,;\\=]*)?)`,
      "g"
    )
    return regex.test(url)
  })
  return istrue
}
// 添加token缓存
const cache = (key: string, value: unknown) => {
  if (!key) {
    Logger.warn("cache 'key' is undefined")
    return
  }
  if (__platform == "android") {
    typeof value == "string"
      ? TinyAPI.cache.set(key, value)
      : TinyAPI.cache.set(key, JSON.stringify(value))
  } else if (__platform == 'mini') {
    typeof value == "string"
      ? miniStorage.set(key, value)
      : miniStorage.set(key, JSON.stringify(value))
  } else {
    typeof value == "string"
      ? sessionStorage.setItem(key, value)
      : sessionStorage.setItem(key, JSON.stringify(value))
  }
}
const parseData = (data?: string | TcacheToken) => {
  if (!data) return data
  try {
    return typeof data === "string" ? JSON.parse(data) : data
  } catch (e) {
    Logger.error("parseData error", data)
    return data
  }
}
// 获取缓存的token
export const getCache = (key: any) => {
  if (!key) return
  let __tokens: TcacheToken | string = ""
  if (__platform == "android") {
    key == "beforeXtoken"
      ? (__tokens = TinyAPI.cache.get(key))
      : (__tokens = parseData(TinyAPI.cache.get(key)))
  } else if (__platform == "mini") {
    key == "beforeXtoken"
      ? (__tokens = miniStorage.get(key))
      : (__tokens = parseData(miniStorage.get(key)))
  } else {
    key == "beforeXtoken"
      ? (__tokens = sessionStorage.getItem(key))
      : (__tokens = parseData(sessionStorage.getItem(key)))
  }
  return __tokens
}

// 请求max-token
export const reqMaxToken = async (xToken: string) => {
  const region = globalInfo.get("regionServerSupported")
  let url = ""
  const maxtoken: { [key: string]: string } = {}

  if (region) {
    url =
      region[region.length - 1] == "/"
        ? region + "auth/token/queryAccessTokenByAppId"
        : region + "/auth/token/queryAccessTokenByAppId"
    // region[region.length - 1] == "/"
    //   ? region + "auth/token/getAccessToken"
    //   : region + "/auth/token/getAccessToken";
    const result = await net.post(url, {
      data: {
        request: {
          refreshToken: xToken,
          // refershToken: xToken,
        },
        context: {
          appId: globalInfo.get("appId") || "",
        },
      },
    })
    if (result?.body?.data) {
      const data = result.body?.data
      maxtoken["max-token"] = data.accessToken
      maxtoken["duration"] = data.ttl
    } else {
      Logger.warn("queryAccessToken error: ", result)
    }
  } else {
    Logger.warn("error:'region' is empty.", region)
  }

  return maxtoken
}

// 计算max-token是否超时
function isTimeOut(after: number, before: number, duration: number) {
  const diffMilliseconds = after - before
  const dura = Math.floor(duration / 60)
  const diffInMinutes = Math.floor(diffMilliseconds / (1000 * 60)) // 将毫秒转换为分钟
  return diffInMinutes >= dura - 10
}

// 判断max-token请求的返回值，更新cache
const isMaxEmpty = (
  max: { [key: string]: string },
  _tokens: { [key: string]: string },
  maxTokenKey: string
) => {
  const now = new Date().getTime()
  let token = Object.assign({}, _tokens)
  if (!isEmpty(max)) {
    token = Object.assign(_tokens, { "max-token": max["max-token"] })
    cache(maxTokenKey, { ...max, beforeTime: now })
  } else {
    token["max-token"] = undefined
    cache(maxTokenKey, {})
  }
  return token
}
/**
 * 登录请求拦截器
 * @param config
 */
export async function loginReqInterceptor<T extends NetRequest>(config: T) {
  const t: Record<string, string> | string | undefined | null = token.get()
  const _token = t?.["x-token"]
  let tokens: Ttokens = {
    "x-org-id": globalInfo.get("tenantCode"),
    "max-app-id": globalInfo.get("appId"),
    "sunmi-appid": globalInfo.get("appId"),
    "x-client-name": globalInfo.get("projectCode"),
  }
  // 漂移场景下 默认先把其他数据先加进去
  if (config?.data?.httpData) {
    config.data.httpData.header = {
      ...tokens,
      ...config.data.httpData.header,
    }
  } else {
    // 非网关请求塞到header里
    config.headers = {
      ...tokens,
      ...config.headers,
    }
  }
  if (isEmpty(t)) {
    Logger.warn("token is empty", t)
    return config
  }
  if (typeof t !== "object") {
    Logger.warn("token type error", t)
    return config
  }
  if (!getCache("beforeXtoken")) {
    cache("beforeXtoken", _token)
  }

  // 静默登录
  if (_token) {
    // tokens = Object.assign(tokens, { "x-token": _token })
    tokens["x-token"] = _token
    if (__platform == "web" || __platform == "preview" || __platform == "mini") {
      const appId = globalInfo.get("appId")
      if (!appId) Logger.warn("appId is empty", appId)
      const now = new Date().getTime()
      const maxTokenKey = `__max-token__${appId}`
      if (isEmpty(getCache(maxTokenKey))) {
        const max = await reqMaxToken(tokens["x-token"])
        tokens = isMaxEmpty(max, tokens, maxTokenKey) as Ttokens
        cache("beforeXtoken", _token)
      } else {
        const cacheToken = getCache(maxTokenKey)
        const beforeXtoken = getCache("beforeXtoken")
        if (beforeXtoken == _token) {
          const { beforeTime, duration } = cacheToken as TcacheToken
          if (isTimeOut(now, beforeTime, duration)) {
            const max = await reqMaxToken(tokens["x-token"])
            tokens = isMaxEmpty(max, tokens, maxTokenKey) as Ttokens
          } else {
            tokens = Object.assign(tokens, {
              "max-token": (cacheToken as TcacheToken)["max-token"],
            })
          }
        } else {
          // x-token对比不一致
          const max = await reqMaxToken(tokens["x-token"])
          tokens = isMaxEmpty(max, tokens, maxTokenKey) as Ttokens
          cache("beforeXtoken", _token)
        }
      }
    }
  } else {
    Logger.error("x-token not found", _token)
    return config
  }
  tokens["max-token"] && (tokens["sunmi-token"] = tokens["max-token"])
  if (config?.data?.httpData) {
    config.data.httpData.header = {
      ...tokens,
      ...config.data.httpData.header,
    }
  } else {
    // 非网关请求塞到header里
    config.headers = {
      ...tokens,
      ...config.headers,
    }
  }
  return {
    ...config,
    headers: {
      ...config.headers,
    },
  }
}

/**
 * 登录返回拦截器
 * @param response
 */
export function loginResInterceptor(response: NetResult<any>): NetResult<any> {
  const code = response.body.code
  const loginRouter = globalInfo.get("login")?.loginRouter
  // 弹出error code对应信息，通过langkey获取
  const popCodeMsg = (code: string | number, isPoptoast = false) => {
    const langKey = `ERROR.cloud.${code}`
    const msg = getSystemErrorLang(langKey)
    if (isPoptoast) {
      if (msg) {
        ui.toast(JSON.stringify(msg))
      } else {
        ui.toast(`errorCode:${langKey},error message not found.`)
      }
    } else {
      Logger.info('loginResInterceptor: ', msg, code)
    }
  }
  // "MID0073003" max-token error code
  if (
    code == -2 ||
    code == 20001 ||
    code == 20000 ||
    code == "MAX0170004" ||
    code == 30000
  ) {
    // TODO: 暂时先DSL清除，未来交由登陆组件处理业务逻辑
    if (__platform === 'android') {
      TinyAPI?.netCache?.clear?.()
    }

    popCodeMsg(code, true)
    if (loginRouter) {
      const timer = setTimeout(() => {
        rootMax.max.router.navigateTo(loginRouter)
        clearTimeout(timer)
      }, 2400)
    } else {
      ui.toast("'loginRouter' is not set!")
    }
  }
  // else {
  //   // popCodeMsg(code.toString(), true)
  // }
  return response
}
